import { Grid } from '@zoopla-eng/zds/grid';
import { Box } from '@zoopla-eng/zds/box';
import { Link } from '@zoopla-eng/zds/link';
import { Divider } from '@zoopla-eng/zds/divider';

import {
    TwitterMedium,
    FacebookMedium,
    LinkedinMedium,
    InstagramMedium,
    YoutubeMedium,
    PinterestMedium
} from '@zoopla-eng/zds/icons';
import { triggerTracking } from '@zoopla/tracking';
import { FC } from 'react';
import { Props } from './Footer.types';
import {
    corporateLinks2Style,
    corporateLinksStyle,
    customGridStyle,
    footerContainerStyle,
    iconWrapperStyle,
    legalTextContainerStyle,
    legalTextStyle,
    linkHeaderStyle,
    listItemStyle,
    listStyle,
    pageStyle,
    socialLinksStyle
} from './Footer.styles.css';
import { fontGroups } from '@zoopla-eng/zds/themes';

const MobileOnlyDivider = () => (
    <Box pt="S500" bpM100={{ display: 'none' }}>
        <Divider direction="horizontal" />
    </Box>
);

const GATracking = (eventLabel: string, clickthrough = true) => {
    triggerTracking({
        event: 'GAEvent',
        eventAction: clickthrough ? 'clickthrough' : 'clickout',
        eventLabel: eventLabel,
        eventCategory: 'footer'
    });
};

type LinkType = {
    linkUrl: string;
    linkCopy: string;
    clickThrough?: boolean;
    external?: boolean;
};
const forSaleLinks: LinkType[] = [
    { linkUrl: '/for-sale/', linkCopy: 'For sale' },
    { linkUrl: '/new-homes/', linkCopy: 'New homes' },
    {
        linkUrl: '/for-sale/commercial/',
        linkCopy: 'Commercial properties for sale'
    },
    { linkUrl: '/overseas/', linkCopy: 'Overseas' },
    { linkUrl: '/find-agents/estate-agents/', linkCopy: 'Find estate agents' },
    { linkUrl: '/travel-time/', linkCopy: 'Travel time search' }
];

const toRentLinks: LinkType[] = [
    { linkUrl: '/to-rent/', linkCopy: 'To rent' },
    {
        linkUrl: '/to-rent/commercial/',
        linkCopy: 'Commercial properties to rent'
    },
    { linkUrl: '/find-agents/letting-agents/', linkCopy: 'Find letting agents' }
];

const newsAndGuidesLinks: LinkType[] = [
    { linkUrl: '/discover/', linkCopy: 'News and guides' },
    { linkUrl: '/schemes/shared-ownership/', linkCopy: 'Shared Ownership' },
    { linkUrl: '/guides/buying-a-home/', linkCopy: 'Buying guides' },
    { linkUrl: '/guides/selling-a-home/', linkCopy: 'Selling guides' },
    { linkUrl: '/discover/renting/', linkCopy: 'Renting guides' }
];

const zooplaForBusiness: LinkType[] = [
    { linkUrl: '/for-agents-and-developers/', linkCopy: 'List with Zoopla' },
    {
        linkUrl: 'https://pro.zoopla.co.uk/',
        linkCopy: 'ZooplaPro',
        clickThrough: false
    },
    {
        linkUrl: 'https://www.altosoftware.co.uk/',
        linkCopy: 'Alto Software',
        clickThrough: false,
        external: true
    }
];

const corporateLinks: LinkType[] = [
    { linkUrl: '/privacy/', linkCopy: 'Privacy' },
    { linkUrl: '/privacy/cookie-policy/', linkCopy: 'Cookies' },
    {
        linkUrl: 'https://help.zoopla.co.uk/hc/en-gb',
        linkCopy: 'Contact us',
        clickThrough: false
    },
    { linkUrl: '/about/', linkCopy: 'About Zoopla' },
    {
        linkUrl: 'https://careers.zoopla.co.uk/',
        linkCopy: 'Careers',
        clickThrough: false
    },
    {
        linkUrl: '/display/',
        linkCopy: 'Display advertising',
        clickThrough: false
    },
    { linkUrl: '/sitemap/', linkCopy: 'Sitemap' }
];

const corporateLinks2: LinkType[] = [
    { linkUrl: '/terms/', linkCopy: 'Terms' },
    { linkUrl: '/members/terms/', linkCopy: 'Member terms' },
    { linkUrl: '/slavery-statement/', linkCopy: 'Slavery statement' },
    { linkUrl: '/tax-strategy/', linkCopy: 'Tax strategy' }
];

const socialLinks = [
    {
        linkUrl: 'https://www.facebook.com/ZooplaUK',
        icon: <FacebookMedium />,
        label: 'Visit the Zoopla Facebook Page'
    },
    {
        linkUrl: 'https://twitter.com/zoopla',
        icon: <TwitterMedium />,
        label: 'Visit the Zoopla Twitter Page'
    },
    {
        linkUrl: 'https://www.linkedin.com/company/zoopla/about',
        icon: <LinkedinMedium />,
        label: 'Visit the Zoopla LinkedIn Page'
    },
    {
        linkUrl: 'https://www.instagram.com/zoopla',
        icon: <InstagramMedium />,
        label: 'Visit the Zoopla Instagram Page'
    },
    {
        linkUrl: 'https://www.youtube.com/user/ZooplaUK',
        icon: <YoutubeMedium />,
        label: 'Visit the Zoopla Youtube Page'
    },
    {
        linkUrl: 'https://uk.pinterest.com/zoopla',
        icon: <PinterestMedium />,
        label: 'Visit the Zoopla Pinterest Page'
    }
];

const SocialLinkIcon = ({
    linkUrl,
    icon,
    label
}: {
    linkUrl: string;
    icon: JSX.Element;
    label: string;
}) => (
    <a
        className={iconWrapperStyle}
        target="_blank"
        rel="noreferrer"
        href={linkUrl}
        aria-label={label}
    >
        {icon}
    </a>
);

const ListItemLink = ({
    linkUrl,
    linkCopy,
    clickThrough = true,
    unweighted,
    external = false
}: LinkType & { unweighted?: boolean }) => (
    <li className={listItemStyle}>
        <Link
            href={linkUrl}
            rel={external ? 'nofollow' : undefined}
            unweighted={unweighted}
            muted
            onClick={() => GATracking(linkCopy.toLowerCase(), clickThrough)}
            disableVisited
        >
            {linkCopy}
        </Link>
    </li>
);

export const Footer: FC<React.PropsWithChildren<Props>> = () => (
    <div className={footerContainerStyle}>
        <div className={pageStyle}>
            <div className={customGridStyle}>
                <Grid.Item area="forSale">
                    <ul className={listStyle}>
                        {forSaleLinks.map((l, i) => (
                            <ListItemLink
                                key={l.linkUrl}
                                unweighted={i > 0}
                                linkCopy={l.linkCopy}
                                linkUrl={l.linkUrl}
                                clickThrough={l.clickThrough}
                                external={l.external}
                            />
                        ))}
                    </ul>
                    <MobileOnlyDivider />
                </Grid.Item>
                <Grid.Item area="toRent">
                    <ul className={listStyle}>
                        {toRentLinks.map((l, i) => (
                            <ListItemLink
                                key={l.linkUrl}
                                unweighted={i > 0}
                                linkCopy={l.linkCopy}
                                linkUrl={l.linkUrl}
                                clickThrough={l.clickThrough}
                                external={l.external}
                            />
                        ))}
                    </ul>
                    <MobileOnlyDivider />
                </Grid.Item>
                <Grid.Item area="newsAndGuides">
                    <ul className={listStyle}>
                        {newsAndGuidesLinks.map((l, i) => (
                            <ListItemLink
                                key={l.linkUrl}
                                unweighted={i > 0}
                                linkCopy={l.linkCopy}
                                linkUrl={l.linkUrl}
                                clickThrough={l.clickThrough}
                                external={l.external}
                            />
                        ))}
                    </ul>
                    <MobileOnlyDivider />
                </Grid.Item>
                <Grid.Item area="zooplaForBusiness">
                    <ul className={listStyle}>
                        <li className={listItemStyle}>
                            <span className={linkHeaderStyle}>
                                Zoopla for business
                            </span>
                        </li>
                        {zooplaForBusiness.map((l) => (
                            <ListItemLink
                                key={l.linkUrl}
                                unweighted={true}
                                linkCopy={l.linkCopy}
                                linkUrl={l.linkUrl}
                                clickThrough={l.clickThrough}
                                external={l.external}
                            />
                        ))}
                    </ul>
                </Grid.Item>
                <Grid.Item area="divider">
                    <Divider direction="horizontal" />
                </Grid.Item>
                <Grid.Item area="corporateLinks1">
                    {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
                    <ul className={corporateLinksStyle} role="list">
                        {corporateLinks.map((l) => (
                            <ListItemLink
                                key={l.linkUrl}
                                linkCopy={l.linkCopy}
                                linkUrl={l.linkUrl}
                                clickThrough={l.clickThrough}
                            />
                        ))}
                    </ul>
                    <MobileOnlyDivider />
                </Grid.Item>
                <Grid.Item area="corporateLinks2">
                    {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
                    <ul className={corporateLinks2Style} role="list">
                        {corporateLinks2.map((l) => (
                            <ListItemLink
                                key={l.linkUrl}
                                linkCopy={l.linkCopy}
                                linkUrl={l.linkUrl}
                                clickThrough={l.clickThrough}
                            />
                        ))}
                    </ul>
                </Grid.Item>
                <Grid.Item area="social">
                    <div className={socialLinksStyle}>
                        {socialLinks.map(({ icon, linkUrl, label }) => (
                            <SocialLinkIcon
                                key={linkUrl}
                                linkUrl={linkUrl}
                                icon={icon}
                                label={label}
                            />
                        ))}
                    </div>
                </Grid.Item>
                <Grid.Item area="copyright">
                    <div className={legalTextContainerStyle}>
                        <p className={`${legalTextStyle} ${fontGroups.knife}`}>
                            © {new Date().getFullYear()} Zoopla Limited. All
                            rights reserved.
                        </p>
                    </div>
                </Grid.Item>
                <Grid.Item area="legal">
                    <p className={`${legalTextStyle} ${fontGroups.knife}`}>
                        Sold house prices provided by Land Registry/Registers of
                        Scotland. © Crown copyright {new Date().getFullYear()}.
                        Our website is completely free for you to use but we may
                        receive a commission from some of the companies we link
                        to on the site. *Zoopla Limited is an appointed
                        representative of Uswitch Limited which is authorised
                        and regulated by the Financial Conduct Authority (FRN
                        312850) to provide this mortgage comparison service and
                        incorporated with company registration number 06074771
                        and registered office at The Cooperage, 5 Copper Row,
                        London SE1 2LH. **Uswitch Limited is authorised and
                        regulated by the Financial Conduct Authority (FCA) under
                        firm reference number 312850
                    </p>
                </Grid.Item>
            </div>
        </div>
    </div>
);
